import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { einvoiceApi } from '@/lib/api/einvoice-api'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'
import { einvoiceKeys, useEInvoice } from '@/hooks/use-einvoice-query'
import {
    invoiceIdentity,
    storeEInvoiceObservation,
    useEInvoiceObservation,
} from '@/hooks/use-einvoice-observation'

type DocumentState = {
    key?: string
    previewToken?: string | null
    url?: string
    source?: string | null
    status?: string | null
    error?: string
    /** MISA accepted the publication but has not finished signing (M-147). */
    stillSigning?: boolean
}

/** Statuses where an unconfirmed document only means MISA is still signing. */
const SIGNING_STATUSES = ['issuing', 'publishing']

const stillSigning = (error: unknown, status?: string) =>
    error instanceof ApiError &&
    error.code === 'EINVOICE_DOCUMENT_UNCONFIRMED' &&
    SIGNING_STATUSES.includes(status ?? '')

export function useEInvoiceDocument(
    orderId: string,
    open: boolean,
    status?: string,
) {
    const client = useQueryClient()
    // Read at answer time only: a status change alone must not reload the PDF.
    const statusRef = useRef(status)
    useEffect(() => {
        statusRef.current = status
    }, [status])
    const detail = useEInvoice(orderId, false)
    const generation = useQuery({
        queryKey: einvoiceKeys.document(orderId),
        queryFn: () => 0,
        enabled: false,
        initialData: 0,
    }).data
    const observation = useEInvoiceObservation(orderId, detail.data)
    const [attempt, setAttempt] = useState(0)
    const [document, setDocument] = useState<DocumentState>({})
    const identity = invoiceIdentity(detail.data)
    const key = JSON.stringify([orderId, identity, generation, attempt, open])
    useEffect(() => {
        setDocument({})
        if (!open) return
        const controller = new AbortController()
        let url: string | undefined
        const current = () =>
            !controller.signal.aborted &&
            identity ===
                invoiceIdentity(
                    client.getQueryData(einvoiceKeys.detail(orderId)),
                )
        einvoiceApi
            .document(orderId, controller.signal)
            .then((result) => {
                if (!current()) return
                storeEInvoiceObservation(client, orderId, result.observation)
                url = URL.createObjectURL(result.blob)
                setDocument({
                    key,
                    previewToken: result.previewToken,
                    url,
                    source: result.source,
                    status: result.status,
                })
            })
            .catch((error: unknown) => {
                if (!current()) return
                // The "unknown" observation of a signing invoice would outlive
                // the issued result, so it is not stored.
                if (stillSigning(error, statusRef.current)) {
                    setDocument({ key, stillSigning: true })
                    return
                }
                if (
                    error &&
                    typeof error === 'object' &&
                    'observation' in error
                )
                    storeEInvoiceObservation(client, orderId, error.observation)
                setDocument({
                    key,
                    error:
                        error instanceof ApiError && error.message
                            ? error.message
                            : m.einvoice_viewer_error(),
                })
            })
        return () => {
            controller.abort()
            if (url) URL.revokeObjectURL(url)
        }
    }, [client, identity, key, open, orderId])
    return {
        ...(document.key === key ? document : {}),
        observation,
        retry: () => setAttempt((value) => value + 1),
    }
}
